import { getUtcDateForMDY } from '@/utils/date-time';
import { AccountAccount, AccountGender } from '@calm/evexia-web-sdk';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from '@rhg/libs/yup';
import { createContext, ReactNode, useContext } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { ageGenderMessages } from './messages';

export interface AgeGenderFormState {
    dob: Date;
    gender?: AccountGender;
}

interface AgeAndGenderFormProviderProps {
    account?: AccountAccount;
    children: ReactNode;
}

export const AgeAndGenderFormContext = createContext<
    UseFormReturn<AgeGenderFormState> | undefined
>(undefined);

export const useAgeAndGenderFormContext = () => {
    const context = useContext(AgeAndGenderFormContext);
    if (!context) {
        throw new Error(
            'useAgeAndGenderFormContext must be used within AgeAndGenderFormProvider'
        );
    }
    return context;
};

export const AgeAndGenderFormProvider = ({
    account,
    children,
}: AgeAndGenderFormProviderProps) => {
    const { formatMessage } = useIntl();
    const methods = useForm<AgeGenderFormState>({
        mode: 'onSubmit',
        defaultValues: {
            dob: account?.dob ? getUtcDateForMDY(account.dob) : undefined,
            gender: account?.gender ? account.gender : undefined,
        },
        resolver: yupResolver(
            yup.object({
                dob: yup
                    .date()
                    .required(formatMessage(ageGenderMessages.dobInputError)),
                gender: yup
                    .string()
                    .oneOf(Object.values(AccountGender))
                    .optional(),
            })
        ),
    });

    return (
        <AgeAndGenderFormContext.Provider value={methods}>
            {children}
        </AgeAndGenderFormContext.Provider>
    );
};
